import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

export interface TransportDefinition {
  id: string;
  name: string;
  transportType: string;
  host: string;
  port: number;
  credentialId: string | null;
  parentTransportId: string | null;
  divisionId: string | null;
  regionId: string;
}

interface TransportDefinitionListResponse {
  transportDefinitions: TransportDefinition[];
}

const backendApi = config.backendApi;

const fetchTransportDefinitions = async (): Promise<TransportDefinition[]> => {
  const response = await axios.get<TransportDefinitionListResponse>(`${backendApi}/api/transport-definitions`, { withCredentials: true });
  return response.data.transportDefinitions;
};

export function useTransportDefinitions(organizationId: string | null | undefined): UseQueryResult<TransportDefinition[]> {
  const contextReady = useActiveContextReady();

  return useQuery<TransportDefinition[]>({
    queryKey: ['transportDefinitions', organizationId],
    queryFn: () => {
      if (!organizationId) {
        throw new Error("organizationId is required to fetch transport definitions.");
      }
      return fetchTransportDefinitions();
    },
    enabled: !!organizationId && contextReady,
  });
}

export interface CreateTransportDefinitionRequest {
  name: string;
  transportType: string;
  host: string;
  port: number;
  credentialId: string | null;
  parentTransportId: string | null;
  regionId: string;
  divisionId: string | null;
}

export function useCreateTransportDefinition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateTransportDefinitionRequest) => {
      const response = await axios.post<TransportDefinition>(
        `${backendApi}/api/transport-definitions`,
        data,
        { withCredentials: true }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['transportDefinitions'] });
    },
  });
}

export interface UpdateTransportDefinitionRequest {
  name?: string;
  transportType?: string;
  host?: string;
  port?: number;
  credentialId?: string | null;
  parentTransportId?: string | null;
  regionId?: string;
  divisionId?: string | null;
}

export function useUpdateTransportDefinition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { id: string } & UpdateTransportDefinitionRequest) => {
      const { id, ...body } = data;
      const response = await axios.put<TransportDefinition>(
        `${backendApi}/api/transport-definitions/${id}`,
        body,
        { withCredentials: true }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['transportDefinitions'] });
    },
  });
}

export function useDeleteTransportDefinition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${backendApi}/api/transport-definitions/${id}`, { withCredentials: true });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['transportDefinitions'] });
    },
  });
}
